/**
 * FrameProfiler — lightweight, debug-gated per-phase frame profiler.
 *
 * It answers the question "where is a dropped frame going?" by timing the
 * individual phases of the game loop (level generation, player+collision,
 * hazard checks, projectiles, particles, render) and pairing that with the
 * two other usual suspects:
 *   - GPU/scene complexity via Three.js `renderer.info` (draw calls, triangles,
 *     and live geometry/texture counts — the latter double as leak detectors), and
 *   - GC pressure via a `performance.memory` heap-delta proxy (allocation rate
 *     and GC frequency).
 *
 * Everything is a no-op unless `enabled` is set (it follows the 'p' debug
 * overlay), so it costs nothing in normal play. When enabled it also emits
 * User Timing marks/measures so a Chrome DevTools Performance recording shows
 * the same phase spans in the "Timings" track.
 *
 * Timings are sampled every frame into rolling windows; the aggregated
 * avg/p95/max shown in the overlay is recomputed on a throttle (computeInterval)
 * to keep the per-frame cost negligible.
 */
export class FrameProfiler {
  constructor({ windowSize = 180, computeInterval = 500 } = {}) {
    this.enabled = false;
    this.windowSize = windowSize; // frames kept per rolling window (~3s at 60fps)
    this.computeInterval = computeInterval; // ms between aggregate recomputes

    // User Timing is used purely so DevTools recordings are legible.
    this.useUserTiming =
      typeof performance !== "undefined" && typeof performance.mark === "function";

    this._marks = new Map(); // open phase name -> start time
    this._samples = new Map(); // phase name -> number[] (ring buffer of ms)
    this._frameSamples = []; // whole-frame ms (ring buffer)
    this._frameStart = 0;

    // Memory / GC proxy (Chrome-only; performance.memory)
    this._lastHeap = null;
    this._heapMax = 0;
    this._allocAccum = 0; // bytes allocated since last aggregate compute
    this._gcAccumCount = 0; // GC events since last aggregate compute
    this._gcTotalCount = 0; // GC events since reset

    // renderer.info snapshot + high-water marks (leak detection)
    this.render = {
      calls: 0,
      triangles: 0,
      geometries: 0,
      textures: 0,
      programs: 0,
    };
    this.renderMax = { geometries: 0, textures: 0 };

    this._cache = null;
    this._lastComputeTime = 0;
  }

  setEnabled(value) {
    value = !!value;
    if (value === this.enabled) return;
    this.enabled = value;
    // Start each profiling session from a clean slate so numbers aren't
    // polluted by the (possibly long) idle period before it was turned on.
    if (value) this.reset();
  }

  reset() {
    this._marks.clear();
    this._samples.clear();
    this._frameSamples.length = 0;
    this._lastHeap = null;
    this._heapMax = 0;
    this._allocAccum = 0;
    this._gcAccumCount = 0;
    this._gcTotalCount = 0;
    this.renderMax = { geometries: 0, textures: 0 };
    this._cache = null;
    this._lastComputeTime =
      typeof performance !== "undefined" ? performance.now() : 0;
  }

  beginFrame() {
    if (!this.enabled) return;
    this._frameStart = performance.now();
  }

  begin(name) {
    if (!this.enabled) return;
    this._marks.set(name, performance.now());
    if (this.useUserTiming) {
      try {
        performance.mark(`${name}-start`);
      } catch (e) {
        /* ignore */
      }
    }
  }

  end(name) {
    if (!this.enabled) return;
    const start = this._marks.get(name);
    if (start === undefined) return; // tolerate unbalanced calls (e.g. early return)
    this._marks.delete(name);
    this._push(name, performance.now() - start);
    if (this.useUserTiming) {
      try {
        performance.measure(name, `${name}-start`);
      } catch (e) {
        /* ignore */
      }
    }
  }

  _push(name, dur) {
    let arr = this._samples.get(name);
    if (!arr) {
      arr = [];
      this._samples.set(name, arr);
    }
    arr.push(dur);
    if (arr.length > this.windowSize) arr.shift();
  }

  /** Snapshot Three.js render stats. Call once per frame after render(). */
  sampleRenderer(renderer) {
    if (!this.enabled || !renderer || !renderer.info) return;
    const info = renderer.info;
    this.render.calls = info.render.calls;
    this.render.triangles = info.render.triangles;
    this.render.geometries = info.memory.geometries;
    this.render.textures = info.memory.textures;
    this.render.programs = info.programs ? info.programs.length : 0;
    if (this.render.geometries > this.renderMax.geometries)
      this.renderMax.geometries = this.render.geometries;
    if (this.render.textures > this.renderMax.textures)
      this.renderMax.textures = this.render.textures;
  }

  /**
   * Sample the JS heap to estimate allocation pressure. This is a proxy:
   * summed positive heap deltas are a lower bound on real allocation (a GC can
   * hide intra-frame churn), but together with GC frequency it's enough to tell
   * whether GC is the cause of jank. No-op outside Chromium.
   */
  sampleMemory() {
    if (!this.enabled) return;
    if (typeof performance === "undefined" || !performance.memory) return;
    const heap = performance.memory.usedJSHeapSize;
    if (this._lastHeap !== null) {
      const delta = heap - this._lastHeap;
      if (delta >= 0) {
        this._allocAccum += delta;
      } else {
        // Heap shrank => a GC ran this frame.
        this._gcAccumCount++;
        this._gcTotalCount++;
      }
    }
    if (heap > this._heapMax) this._heapMax = heap;
    this._lastHeap = heap;
  }

  endFrame() {
    if (!this.enabled) return;
    this._frameSamples.push(performance.now() - this._frameStart);
    if (this._frameSamples.length > this.windowSize) this._frameSamples.shift();
    // Keep the User Timing entry buffer from growing unbounded. This only
    // clears the queryable buffer; entries already captured by an in-progress
    // DevTools recording are unaffected.
    if (this.useUserTiming) {
      performance.clearMarks();
      performance.clearMeasures();
    }
  }

  _stats(arr) {
    const n = arr.length;
    if (n === 0) return { avg: 0, p95: 0, max: 0 };
    let sum = 0;
    let max = 0;
    for (let i = 0; i < n; i++) {
      sum += arr[i];
      if (arr[i] > max) max = arr[i];
    }
    const sorted = arr.slice().sort((a, b) => a - b);
    const p95 = sorted[Math.min(n - 1, Math.floor(n * 0.95))];
    return { avg: sum / n, p95, max };
  }

  /**
   * Aggregated stats for display. Recomputed at most every computeInterval ms;
   * returns the cached result in between so calling it every frame is cheap.
   */
  getStats() {
    if (!this.enabled) return null;
    const now = performance.now();
    if (this._cache && now - this._lastComputeTime < this.computeInterval) {
      return this._cache;
    }

    const elapsedSec = Math.max(1e-3, (now - this._lastComputeTime) / 1000);
    const MB = 1048576;

    const phases = {};
    for (const [name, arr] of this._samples) phases[name] = this._stats(arr);

    const memorySupported =
      typeof performance !== "undefined" && !!performance.memory;

    this._cache = {
      frame: this._stats(this._frameSamples),
      phases,
      render: { ...this.render },
      renderMax: { ...this.renderMax },
      memory: {
        supported: memorySupported,
        usedMB: this._lastHeap !== null ? this._lastHeap / MB : null,
        maxMB: this._heapMax / MB,
        allocMBs: this._allocAccum / MB / elapsedSec, // MB/s (proxy)
        gcPerSec: this._gcAccumCount / elapsedSec,
        gcTotal: this._gcTotalCount,
      },
    };

    // Reset per-interval accumulators so the next rate covers a fresh window.
    this._allocAccum = 0;
    this._gcAccumCount = 0;
    this._lastComputeTime = now;
    return this._cache;
  }

  /** Build a console.table-friendly map of phase timings. */
  getSummary() {
    const s = this.getStats();
    if (!s) return {};
    const round = (v) => Math.round(v * 1000) / 1000;
    const rows = {};
    const names = Object.keys(s.phases).sort(
      (a, b) => s.phases[b].avg - s.phases[a].avg
    );
    for (const name of names) {
      const p = s.phases[name];
      rows[name] = {
        "avg ms": round(p.avg),
        "p95 ms": round(p.p95),
        "max ms": round(p.max),
      };
    }
    rows["— FRAME —"] = {
      "avg ms": round(s.frame.avg),
      "p95 ms": round(s.frame.p95),
      "max ms": round(s.frame.max),
    };
    return rows;
  }

  /** Dump a full snapshot to the console (phases table + render + memory). */
  logSummary() {
    const s = this.getStats();
    if (!s) {
      console.log("[FrameProfiler] disabled — enable the debug overlay first.");
      return;
    }
    console.group("[FrameProfiler] snapshot");
    if (console.table) {
      console.table(this.getSummary());
    } else {
      console.log(this.getSummary());
    }
    console.log("render.info:", {
      drawCalls: s.render.calls,
      triangles: s.render.triangles,
      geometries: `${s.render.geometries} (peak ${s.renderMax.geometries})`,
      textures: `${s.render.textures} (peak ${s.renderMax.textures})`,
      programs: s.render.programs,
    });
    if (s.memory.supported) {
      console.log("memory:", {
        usedMB: s.memory.usedMB && Math.round(s.memory.usedMB),
        peakMB: Math.round(s.memory.maxMB),
        "alloc MB/s (proxy)": Math.round(s.memory.allocMBs * 10) / 10,
        "GC/s": Math.round(s.memory.gcPerSec * 10) / 10,
        gcTotal: s.memory.gcTotal,
      });
    } else {
      console.log(
        "memory: performance.memory unavailable (Chromium-only) — use the DevTools Memory/Performance panel for GC."
      );
    }
    console.groupEnd();
  }
}
